/**
 * @module OperationProgressParts
 * @purpose Presentational sub-components for OperationProgress: loading, error,
 *   unknown-operation, determinate/indeterminate bars, and the terminal
 *   completion dialogue.
 * @depends operation-progress.helpers, react-i18next TFunction
 * @dependedOnBy OperationProgress
 *
 * @rules
 * - State: all state passed via props from the parent -- purely presentational
 * - Errors: role="alert" on error/unknown states; role="status" on loading
 * - Theming: CSS custom property tokens only (var(--accent), var(--text), ...)
 */

import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { computePercent, summariseOutcome } from './operation-progress.helpers'

const COUNTER_FONT: React.CSSProperties = { fontFamily: 'var(--font-mono)' }
const TRACK_STYLE: React.CSSProperties = {
  background: 'var(--border)',
  borderRadius: '9999px',
  height: '0.5rem',
  overflow: 'hidden',
}
const FILL_STYLE: React.CSSProperties = {
  background: 'var(--accent)',
  height: '100%',
  transition: 'width 300ms ease',
}

export function LoadingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div role="status" aria-busy="true" className="text-sm" style={{ color: 'var(--text-dim)' }}>
      {t('operationProgress.checking')}
    </div>
  )
}

interface ErrorStateProps {
  message: string
  onRetry: () => void
  t: TFunction<'cases'>
}

export function ErrorState({ message, onRetry, t }: ErrorStateProps): ReactElement {
  return (
    <div
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50"
    >
      <p className="text-sm text-red-700 dark:text-red-300">{t('operationProgress.pollError')}</p>
      <p className="mt-1 text-xs text-red-600 dark:text-red-400">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 text-xs font-medium text-red-700 hover:underline dark:text-red-300"
      >
        {t('operationProgress.retry')}
      </button>
    </div>
  )
}

export function UnknownOperationState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div
      role="alert"
      className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40"
    >
      <p className="text-sm text-amber-800 dark:text-amber-300">
        {t('operationProgress.unknownOperation')}
      </p>
    </div>
  )
}

interface DeterminateBarProps {
  unitsDone: number
  unitsTotal: number
  stepLabel: string | null
  t: TFunction<'cases'>
}

export function DeterminateBar({ unitsDone, unitsTotal, stepLabel, t }: DeterminateBarProps): ReactElement {
  const percent = computePercent(unitsDone, unitsTotal)
  return (
    <div aria-live="polite" className="space-y-1.5">
      {stepLabel && <p className="text-xs" style={{ color: 'var(--text-dim)' }}>{stepLabel}</p>}
      <div style={TRACK_STYLE}>
        <div
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{ ...FILL_STYLE, width: `${percent.toString()}%` }}
        />
      </div>
      <p className="text-xs" style={{ ...COUNTER_FONT, color: 'var(--text-dim)' }}>
        {t('operationProgress.percentComplete', { percent })}
      </p>
    </div>
  )
}

interface IndeterminateBarProps {
  unitsDone: number
  stepLabel: string | null
  t: TFunction<'cases'>
}

export function IndeterminateBar({ unitsDone, stepLabel, t }: IndeterminateBarProps): ReactElement {
  return (
    <div aria-live="polite" className="space-y-1.5">
      {stepLabel && <p className="text-xs" style={{ color: 'var(--text-dim)' }}>{stepLabel}</p>}
      <div style={TRACK_STYLE} role="progressbar" aria-valuetext={t('operationProgress.inProgress')}>
        <div className="operation-progress-indeterminate-fill" style={FILL_STYLE} />
      </div>
      <p className="text-xs" style={{ ...COUNTER_FONT, color: 'var(--text-dim)' }}>
        {t('operationProgress.unitsDone', { count: unitsDone })}
      </p>
    </div>
  )
}

interface CompletionDialogueProps {
  succeeded: boolean
  outcomeSummary: unknown
  onAcknowledge: () => void
  t: TFunction<'cases'>
}

export function CompletionDialogue({
  succeeded,
  outcomeSummary,
  onAcknowledge,
  t,
}: CompletionDialogueProps): ReactElement {
  const summary = summariseOutcome(outcomeSummary)
  const headline = succeeded ? t('operationProgress.completedTitle') : t('operationProgress.failedTitle')
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="operation-progress-completion-title"
      className="rounded-lg border p-4"
      style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}
    >
      <h3
        id="operation-progress-completion-title"
        className="text-sm font-semibold"
        style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}
      >
        {headline}
      </h3>
      {summary.primary && (
        <p className="mt-1 text-sm" style={{ color: 'var(--text-dim)' }}>
          {summary.primary}
        </p>
      )}
      {summary.entries.length > 0 && (
        <dl className="mt-2 space-y-0.5 text-xs" style={COUNTER_FONT}>
          {summary.entries.map((entry) => (
            <div key={entry.label} className="flex gap-2">
              <dt style={{ color: 'var(--text-dim)' }}>{entry.label}:</dt>
              <dd style={{ color: 'var(--text)' }}>{entry.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <button
        type="button"
        onClick={onAcknowledge}
        className="mt-3 rounded-lg px-4 py-1.5 text-xs font-medium"
        style={{ background: 'var(--accent)', color: 'var(--btn-primary-text, #fff)', fontFamily: 'var(--font-mono)' }}
      >
        {t('operationProgress.acknowledge')}
      </button>
    </div>
  )
}
